import { ChangeDetectionStrategy, Component, computed, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';

import { AddressesQueriesService } from 'src/shared/services/addresses-queries.service';
import { MonthlyFinanceQueriesService } from 'src/shared/services/monthly-finance-queries.service';
import {
  MonthAttribution,
  MonthlyFinanceAudit,
  MonthlyFinanceKind,
} from 'src/models/monthly-finance.model';

interface FinanceHistoryDialogData {
  addressId: number | null;
  kind?: MonthlyFinanceKind;
  /** The list holds only this reading's overrides, so each actual says which one. */
  monthAttribution?: MonthAttribution;
  year?: number;
}

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

@Component({
  selector: 'tidy-finance-history-dialog',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './finance-history-dialog.component.html',
  styleUrls: ['./finance-history-dialog.component.scss'],
})
export class FinanceHistoryDialogComponent implements OnInit {
  readonly addressesQueries = inject(AddressesQueriesService);
  readonly monthlyFinance = inject(MonthlyFinanceQueriesService);

  /** Populated by the dynamic-dialog-container after construction (before ngOnInit). */
  data: FinanceHistoryDialogData = { addressId: null };

  readonly audits = computed(() => this.monthlyFinance.audits());
  readonly isLoading = computed(() => this.monthlyFinance.auditsQuery.isPending());
  readonly isLoadingMore = computed(() => this.monthlyFinance.auditsQuery.isFetchingNextPage());
  readonly hasMore = computed(() => !!this.monthlyFinance.auditsQuery.hasNextPage());

  actualsLabel = 'Actuals';

  ngOnInit(): void {
    this.actualsLabel = this.data.monthAttribution === 'cash' ? 'Actuals (cash)' : 'Actuals (accrual)';
  }

  loadMore(): void {
    this.monthlyFinance.loadMoreAudits();
  }

  monthLabel(year: number, month: number): string {
    return `${MONTHS_SHORT[month - 1]} ${year}`;
  }

  propertyLabel(addressId: number): string {
    const addr = this.addressesQueries.getAddressById(addressId);
    return addr?.['address_name'] || addr?.address1 || `Property #${addressId}`;
  }

  formatTimestamp(iso: string): string {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  }

  formatAmount(cents: number | null): string {
    if (cents === null) {return '—';}
    const dollars = Math.abs(cents) / 100;
    const sign = cents < 0 ? '-' : '';
    return `${sign}$${dollars.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  }

  actorLabel(audit: MonthlyFinanceAudit): string {
    if (!audit.user) {return 'System';}
    return audit.user.type ?? `User #${audit.user.id}`;
  }

  actionTagClass(action: MonthlyFinanceAudit['action']): string {
    return `tag-${action}`;
  }
}
